import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeSlash, Check } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const MEDIA = {
  brotherhood: 'https://images.pexels.com/photos/4261796/pexels-photo-4261796.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920',
};

const BUILDING_OPTIONS = [
  'Business / Entrepreneurship',
  'Career / Corporate',
  'Ministry / Faith',
  'Fitness / Health',
  'Family / Relationships',
  'Creative / Arts',
  'Real Estate / Investing',
  'Other'
];

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [building, setBuilding] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    const result = await signup(name, email, password, building || null, referralCode || null);
    setLoading(false);
    
    if (result.success) {
      toast.success('Welcome to the Brotherhood');
      navigate('/hub', { replace: true });
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F0E6] flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Logo */}
          <Link to="/" className="block mb-12">
            <h1 className="text-3xl font-bold tracking-tighter text-[#F4F0E6]">POWR</h1>
          </Link>

          {/* Form Header */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full mb-4">
              <span className="text-[#10B981] font-bold text-xs">✓ FREE FOREVER</span>
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A661] mb-3">
              Join the Brotherhood
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
              Create Your Free Account
            </h2>
            <p className="text-white/50">
              Join thousands of brothers building together — always free
            </p>
          </div>

          {/* Referral Badge */}
          {referralCode && (
            <div className="mb-6 flex items-center gap-2 bg-[#C4A661]/10 border border-[#C4A661]/30 px-4 py-3">
              <Check size={16} className="text-[#C4A661]" weight="bold" />
              <span className="text-sm text-[#C4A661]">Referral code applied</span>
            </div>
          )}

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/50 mb-3">
                Full Name
              </label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="bg-white/5 border-white/10 text-[#F4F0E6] placeholder:text-white/30 h-14 px-4 rounded-none focus:border-[#C4A661] focus:ring-[#C4A661]"
                data-testid="name-input"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/50 mb-3">
                Email Address
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-white/5 border-white/10 text-[#F4F0E6] placeholder:text-white/30 h-14 px-4 rounded-none focus:border-[#C4A661] focus:ring-[#C4A661]"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/50 mb-3">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="bg-white/5 border-white/10 text-[#F4F0E6] placeholder:text-white/30 h-14 px-4 pr-12 rounded-none focus:border-[#C4A661] focus:ring-[#C4A661]"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-white/50 mb-3">
                What Are You Building? <span className="text-white/30">(Optional)</span>
              </label>
              <select
                value={building}
                onChange={(e) => setBuilding(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-[#F4F0E6] h-14 px-4 rounded-none focus:border-[#C4A661] focus:ring-[#C4A661] focus:outline-none"
                data-testid="building-select"
              >
                <option value="" className="bg-[#121212]">Select your focus...</option>
                {BUILDING_OPTIONS.map((option) => (
                  <option key={option} value={option} className="bg-[#121212]">{option}</option>
                ))}
              </select>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C4A661] text-[#050505] font-bold h-14 rounded-none hover:bg-[#D4B671] transition-all disabled:opacity-50"
                data-testid="signup-button"
              >
                {loading ? 'Creating Account...' : 'Join Free Forever'}
                <ArrowRight size={18} className="ml-2" />
              </Button>
              <p className="text-center text-xs text-white/30 mt-3">No credit card required</p>
            </div>
          </form>

          {/* Terms */}
          <p className="mt-6 text-xs text-white/30 leading-relaxed">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </p>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-white/30">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Sign In Link */}
          <div className="text-center">
            <p className="text-white/50 mb-4">
              Already have an account?
            </p>
            <Link to="/login">
              <Button
                variant="outline"
                className="w-full border-white/20 text-[#F4F0E6] hover:border-[#C4A661] hover:text-[#C4A661] h-14 rounded-none transition-all"
                data-testid="login-link"
              >
                Sign In
              </Button>
            </Link>
          </div>

          {/* Back to Home */}
          <div className="mt-8 text-center">
            <Link to="/" className="text-xs uppercase tracking-widest text-white/30 hover:text-white/60 transition-colors">
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${MEDIA.brotherhood})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#050505]" />
        <div className="absolute inset-0 bg-[#050505]/30" />
        
        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="max-w-md ml-auto">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C4A661] mb-4">
              Free Membership Includes
            </p>
            <div className="space-y-4">
              {[
                'Private community of ambitious brothers',
                'Free training courses',
                'Daily devotionals & resources',
                'Connect with like-minded men'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Check size={16} className="text-[#10B981]" weight="bold" />
                  <span className="text-white/80">{item}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-xs text-white/50 mb-2">Want even more?</p>
              <Link to="/inner-circle-sales" className="text-[#C4A661] text-sm font-medium hover:text-[#D4B671] transition-colors">
                Explore Inner Circle →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
